import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Highcharts from 'highcharts'
import { BAR_CHART_COLORS } from '../../Helpers/ChartConstants.js'
import { findDisplayableBenchmarks } from '../../Helpers/ETFUtility.js'
import {
  ETF_FUND,
  ETF_INDEX_FUND,
  CLOSED_END_FUND,
  FUTURE_SCHOLAR_529,
} from '../../Helpers/FundTypes.js'
import { formatDateStandard } from '../../../../global/formatters/string-formatter.js'

class PerformanceTotalReturn extends Component {
  constructor(props) {
    super(props)

    this.state = {
      selectedTab: 'total-return-chart',
      selectedInterval: 'Monthly',
      selectedTotalReturnPerformance: this.props.totalReturnPerformance[0],
    }

    this.inceptionLabel =
      props.fundGroup === FUTURE_SCHOLAR_529
        ? 'Since incept. '
        : 'Since fund incept. '
  }

  componentDidMount() {
    // patch for highcharts bug with flatten call
    Highcharts.wrap(
      Highcharts.Axis.prototype,
      'getPlotLinePath',
      function (proceed) {
        const path = proceed.apply(
          this,
          Array.prototype.slice.call(arguments, 1)
        )
        if (path) {
          path.flat = false
        }
        return path
      }
    )

    this.renderHighCharts()
  }

  componentDidUpdate() {
    this.renderHighCharts()
  }

  renderTable(selectedTotalReturnPerformance) {
    const benchmarks = this.getFilteredBenchmarks(
      selectedTotalReturnPerformance.totalReturnPerfDetails
    )
    const { fundGroup } = this.props

    return (
      <table
        id="totalReturnPerformanceTable"
        className="Table Table--reversed Table--striped"
      >
        <thead className="Table-head">
          <tr className="Table-row">
            <th className="Table-header"></th>
            <th
              className="Table-header Table-header--bracketed u-textCenter"
              colSpan={fundGroup !== CLOSED_END_FUND ? 3 : 2}
            >
              Cumulative (%)
            </th>
            <th
              className="Table-header Table-header--bracketed u-textCenter"
              colSpan="5"
            >
              Average Annual (%)
            </th>
          </tr>
          <tr className="Table-row">
            <th className="Table-header u-textRight"></th>
            <th className="Table-header u-textRight">1 Month</th>
            <th className="Table-header u-textRight">3 Month</th>
            {fundGroup !== CLOSED_END_FUND && (
              <th className="Table-header u-textRight">YTD</th>
            )}
            <th className="Table-header u-textRight">1 Year</th>
            <th className="Table-header u-textRight">3 Year</th>
            <th className="Table-header u-textRight">5 Year</th>
            <th className="Table-header u-textRight">10 Year</th>
            <th className="Table-header u-textRight">
              {this.inceptionLabel}
              <br />
              <small className="u-textNormal">
                {formatDateStandard(selectedTotalReturnPerformance.incepDate)}
              </small>
            </th>
          </tr>
        </thead>
        <tbody className="Table-body">
          {benchmarks.map((perfDetail, index) => {
            return (
              <tr className="Table-row" key={`trp${index}`}>
                <td className="Table-cell">{perfDetail.label}</td>
                <td className="Table-cell u-textRight">
                  {perfDetail.oneMonth}
                </td>
                <td className="Table-cell u-textRight">
                  {perfDetail.threeMonth}
                </td>
                {fundGroup !== CLOSED_END_FUND && (
                  <td className="Table-cell u-textRight">{perfDetail.ytd}</td>
                )}
                <td className="Table-cell u-textRight">{perfDetail.oneYear}</td>
                <td className="Table-cell u-textRight">
                  {perfDetail.threeYear}
                </td>
                <td className="Table-cell u-textRight">
                  {perfDetail.fiveYear}
                </td>
                <td className="Table-cell u-textRight">{perfDetail.tenYear}</td>
                <td className="Table-cell u-textRight">
                  {perfDetail.sinceIncep}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    )
  }

  setSelectedInterval = (event) => {
    const selectedValue = event.currentTarget.value

    this.setState({
      selectedInterval: selectedValue,
      selectedTotalReturnPerformance:
        this.getSelectedTotalReturnPerformanceTimeInterval(
          this.props.totalReturnPerformance,
          selectedValue
        ),
    })
  }

  setSelectedTab = (event) => {
    event.preventDefault()

    const { toggleViewClass } = event.currentTarget.dataset

    this.setState({
      selectedTab: toggleViewClass,
    })
  }

  getFilteredBenchmarks(totalReturnPerfDetails) {
    let filteredBenchmarks

    if (
      this.props.fundGroup === ETF_FUND ||
      this.props.fundGroup === ETF_INDEX_FUND
    ) {
      // if ETF, return array with only first index before passing to Highcharts
      filteredBenchmarks = findDisplayableBenchmarks(totalReturnPerfDetails)
    } else if (this.props.fundGroup === CLOSED_END_FUND) {
      filteredBenchmarks = this.getNoBenchMarks(totalReturnPerfDetails)
    } else {
      filteredBenchmarks = this.getValidBenchmark(totalReturnPerfDetails)
    }

    return filteredBenchmarks
  }

  getNoBenchMarks(totalReturnPerfDetails) {
    return totalReturnPerfDetails.filter((benchmark) => {
      return benchmark.benchmark === 'no'
    })
  }

  /**
   * Can't believe I carried this HACK over... :(
   * Assume the first month, if its a dash, then we don't show that row....
   * @param {*} totalReturnPerfDetails
   */
  getValidBenchmark(totalReturnPerfDetails) {
    return totalReturnPerfDetails.filter((benchmark) => {
      return benchmark.oneMonth !== '-'
    })
  }

  getSelectedTotalReturnPerformanceTimeInterval = (
    totalReturnPerformance,
    selectedValue
  ) => {
    const selectedInterval = selectedValue || this.state.selectedInterval
    let selectedPerformance = null

    if (!selectedInterval) {
      return null
    }

    totalReturnPerformance.forEach((selectedTotalReturnPerformance) => {
      if (selectedTotalReturnPerformance.intervalType === selectedInterval) {
        selectedTotalReturnPerformance.totalReturnPerfDetails =
          this.getFilteredBenchmarks(
            selectedTotalReturnPerformance.totalReturnPerfDetails
          )
        selectedPerformance = selectedTotalReturnPerformance
      }
    })

    return selectedPerformance
  }

  getHighChartsData = (totalReturnPerfDetails) => {
    const { fundGroup } = this.props
    const graphData = []

    const benchmarks = this.getFilteredBenchmarks(totalReturnPerfDetails)

    benchmarks.forEach((perfDetail) => {
      const name = perfDetail.label
      const dataArray = []

      dataArray.push(Number(perfDetail.oneMonth))
      dataArray.push(Number(perfDetail.threeMonth))

      if (fundGroup !== CLOSED_END_FUND) {
        dataArray.push(Number(perfDetail.ytd))
      }

      dataArray.push(Number(perfDetail.oneYear))
      dataArray.push(Number(perfDetail.threeYear))
      dataArray.push(Number(perfDetail.fiveYear))
      dataArray.push(Number(perfDetail.tenYear))
      dataArray.push(Number(perfDetail.sinceIncep))

      graphData.push({ name: name, data: dataArray })
    })

    return graphData
  }

  renderHighCharts = () => {
    const chartData = this.getHighChartsData(
      this.state.selectedTotalReturnPerformance.totalReturnPerfDetails
    )
    const { fundGroup } = this.props

    Highcharts.chart('total-return-performance-chart', {
      chart: {
        spacing: [22, 0, 10, 0],
        style: {
          fontFamily: 'Roboto Condensed',
          fontSize: '14px',
        },
        type: 'column',
      },
      colors: BAR_CHART_COLORS,
      credits: false,
      legend: {
        itemMarginBottom: 5,
        itemStyle: {
          color: 'rgb(35, 31, 32)',
          fontSize: '16px',
          lineHeight: '1em',
        },
        margin: 10,
        symbolPadding: 4,
        symbolHeight: 12,
        symbolRadius: 12,
      },
      plotOptions: {
        column: {
          borderWidth: 0,
          pointPadding: 0,
          states: {
            hover: {
              brightness: 0,
            },
          },
        },
      },
      series: chartData,
      title: { text: '' },
      tooltip: {
        backgroundColor: 'rgba(255,255,255, 0.95)',
        borderColor: 'rgba(35, 31, 32, 0.3)',
        borderRadius: 5,
        footerFormat: '</table>',
        headerFormat:
          '<h4 class="u-marginBottom2gu">{point.key}</h4>' +
          '<table class="Table" style="width: 300px;">',
        padding: 10,
        pointFormat:
          '<tr class="Table-row" style="background-color: transparent;">' +
          '<td class="Table-cell u-paddingBottom0 u-paddingLeft0 u-paddingRight0 u-paddingTop2gu u-textTight" style="width: 10px;">' +
          '<hr class="Bullet" style="color: {series.color};">' +
          '</td>' +
          '<td class="Table-cell u-paddingBottom0 u-paddingLeft1gu u-paddingRight4gu u-paddingTop2gu u-textTight">' +
          '{series.name}' +
          '</td>' +
          '<td class="Table-cell u-paddingBottom0 u-paddingHoriz0 u-paddingTop2gu u-textRight u-textTight">' +
          '{point.y:.2f}%' +
          '</td>' +
          '</tr>',
        shadow: false,
        shared: true,
        useHTML: true,
      },
      xAxis: {
        categories:
          fundGroup !== CLOSED_END_FUND
            ? [
                '1-month',
                '3-month',
                'YTD',
                '1-year',
                '3-year',
                '5-year',
                '10-year',
                this.inceptionLabel +
                  formatDateStandard(
                    this.state.selectedTotalReturnPerformance.incepDate
                  ),
              ]
            : [
                '1-month',
                '3-month',
                '1-year',
                '3-year',
                '5-year',
                '10-year',
                this.inceptionLabel +
                  formatDateStandard(
                    this.state.selectedTotalReturnPerformance.incepDate
                  ),
              ],
        crosshair: { color: 'rgba(35, 31, 32, 0.05)' },
        labels: {
          style: {
            color: 'rgba(35, 31, 32, 0.60)',
            fontSize: '14px',
          },
        },
        lineWidth: 0,
        plotLines: [
          {
            value: -0.5,
            zIndex: 2,
            color: 'transparent',
            label: {
              rotation: 0,
              style: {
                color: 'rgba(35, 31, 32, 0.60)',
                fontSize: '14px',
              },
              text: 'Cumulative',
              x: 0,
              y: -12,
            },
          },
          {
            color: 'rgba(35, 31, 32, 0.20)',
            dashStyle: 'dash',
            label: {
              rotation: 0,
              style: {
                color: 'rgba(35, 31, 32, 0.60)',
                fontSize: '14px',
              },
              text: 'Average Annual',
              x: 0,
              y: -12,
            },
            value: fundGroup !== CLOSED_END_FUND ? 2.5 : 1.5,
            width: 1,
            zIndex: 2,
          },
        ],
        tickWidth: 0,
      },
      yAxis: {
        labels: {
          format: '{value}%',
          style: {
            color: 'rgba(35, 31, 32, 0.60)',
            fontSize: '14px',
          },
        },
        gridLineColor: 'rgba(35, 31, 32, 0.10)',
        lineWidth: 0,
        minorTicks: true,
        title: { text: '' },
      },
    })
  }

  render() {
    const totalReturnPerformance = this.state.selectedTotalReturnPerformance

    return (
      <div className="u-paddingBottom8gu" id="total-return-performance">
        <div className="Grid Grid--alignMiddle u-flexJustifyBetween u-marginBottom4gu">
          <div className="Content u-sizeFit u-sm-paddingBottom3gu u-sm-sizeFull">
            <h3 className="u-marginBottom0">
              Total Return Performance (%){' '}
              <span
                className="u-sm-paddingTop1gu u-sm-block"
                id="total-return-performance-title"
              >
                as of {formatDateStandard(totalReturnPerformance.asOfDate)}
              </span>
            </h3>
          </div>
          <div className="u-sizeFit u-flex u-sm-sizeFull">
            <div
              className="Button-group view-hide-show-control"
              data-parent-view-class="total-return-performance"
              data-default-view-class="total-return-chart"
              data-selected-class="is-selected"
            >
              <a
                href="#"
                className={`Button Button--secondary Button--small data-visualization-toggle chart-toggle ${
                  this.state.selectedTab === 'total-return-chart'
                    ? 'is-selected u-textColorWhite'
                    : ''
                }`}
                onClick={this.setSelectedTab}
                data-toggle-view-class="total-return-chart"
              >
                Chart
              </a>
              <a
                href="#"
                className={`Button Button--secondary Button--small data-visualization-toggle chart-toggle ${
                  this.state.selectedTab === 'total-return-table'
                    ? 'is-selected u-textColorWhite'
                    : ''
                }`}
                onClick={this.setSelectedTab}
                data-toggle-view-class="total-return-table"
              >
                Table
              </a>
            </div>
            <div className="Dropdown u-marginLeft2gu">
              <select
                className="Dropdown-menu u-inlineBlock"
                id="total-return-performance-time-dropdown"
                onChange={this.setSelectedInterval}
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
              </select>
            </div>
          </div>
        </div>
        <div
          id="total-return-performance-chart"
          className={`total-return-performance total-return-chart ${
            this.state.selectedTab === 'total-return-chart' ? '' : 'u-hidden'
          }`}
        ></div>
        <div
          id="total-return-performance-table"
          className={`u-overflowX total-return-performance total-return-table ${
            this.state.selectedTab === 'total-return-table' ? '' : 'u-hidden'
          }`}
        >
          {this.renderTable(totalReturnPerformance)}
        </div>
      </div>
    )
  }
}

PerformanceTotalReturn.propTypes = {
  totalReturnPerformance: PropTypes.array.isRequired,
  fundGroup: PropTypes.string.isRequired,
}

export default PerformanceTotalReturn
